import { DEFAULT_CURRENCY_CODE, formatCurrency } from '../money';

/**
 * 🔒 The Deal's budget (piece 19; decision 0664): a Target and a Maximum, in
 * integer cents, saved together from the Intent card's Budget row. A Target is
 * required and a Maximum is not; the newest saved pair is the budget today and
 * every pair saved is a `DealBudgetRevision` row the history reads.
 */
export interface DealBudgetPair {
  targetCents: number;
  maximumCents: number | null;
}

/** A pair as a form holds it, with the Target possibly not typed yet. */
export interface DealBudgetDraft {
  targetCents: number | null;
  maximumCents: number | null;
}

const money = (amount: number) => formatCurrency({ amount, currencyCode: DEFAULT_CURRENCY_CODE });

/**
 * Why a pair cannot be saved, or null where it can. 🔨 A Maximum below the
 * Target is refused by name, so the person reads which figure to move; the
 * card asks this before it sends and the server asks it whatever is sent.
 */
export function dealBudgetProblem(draft: DealBudgetDraft): string | null {
  if (draft.targetCents === null || !Number.isFinite(draft.targetCents)) return 'Budget needs a Target';
  if (!Number.isInteger(draft.targetCents) || (draft.maximumCents !== null && !Number.isInteger(draft.maximumCents))) return 'a budget figure is a whole number of cents';
  if (draft.targetCents < 0) return 'the Target is never below zero';
  if (draft.maximumCents !== null && draft.maximumCents < draft.targetCents) {
    return `the Maximum ${money(draft.maximumCents)} is below the Target ${money(draft.targetCents)}; raise the Maximum or lower the Target`;
  }
  return null;
}

/**
 * The pair written out, which is the note the stage gate reads on the Budget
 * row: *Target $80,000.00 · Maximum $95,000.00*, or the Target alone.
 */
export function dealBudgetSummary(pair: DealBudgetPair): string {
  return pair.maximumCents === null ? `Target ${money(pair.targetCents)}` : `Target ${money(pair.targetCents)} · Maximum ${money(pair.maximumCents)}`;
}

/** Whether two pairs are the same pair: saving the pair already standing appends nothing. */
export function sameDealBudget(a: DealBudgetPair | null, b: DealBudgetPair | null): boolean {
  if (a === null || b === null) return a === b;
  return a.targetCents === b.targetCents && a.maximumCents === b.maximumCents;
}

/**
 * The note a save writes, or null to leave the note as it stands. 🔨 A note a
 * person typed before the row changed shape is kept: the server rewrites the
 * note only while it is blank or still reads the previous pair's summary.
 */
export function dealBudgetNoteAfter(currentNote: string | null, previous: DealBudgetPair | null, next: DealBudgetPair): string | null {
  const note = currentNote?.trim() ?? '';
  if (note === '' || (previous !== null && note === dealBudgetSummary(previous))) return dealBudgetSummary(next);
  return null;
}

/** The typed note the card reads beneath the inputs, or null where the note is the pair's own summary or blank. */
export function typedDealBudgetNote(currentNote: string | null, standing: DealBudgetPair | null): string | null {
  const note = currentNote?.trim() ?? '';
  if (note === '') return null;
  if (standing !== null && note === dealBudgetSummary(standing)) return null;
  return currentNote;
}
